import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { useAuth } from "../../../services/useAuth";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import { useTitle } from "../../../hooks/useTitle";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Button from "../../../components/BaseUI/Button";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { Toast, ToastContainer } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { Dropdown, DropdownContent, DropdownItem } from "../../../components/BaseUI/Dropdown";
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiShield } from "react-icons/fi";
import { approvalPlugins } from "../../../approvals/registry";
import { APPROVES_PAGE_PATH } from "../../../config/approvalAccess";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";


const emptyProfile = {
  name: "",
  description: "",
  pages: [],
  machineAccess: [],
  approvalAccess: [],
  fullAccess: false,
};

const UserProfiles = () => {
  useTitle("Rovion MES - Kullanıcı Profilleri");
  const navigate = useNavigate();
  const { getToken, getUserFromToken } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toasts, setToasts] = useState([]);



  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Kullanıcı Profilleri" },
  ];

  const headers = [
    {
      key: "name",
      name: "Profil Adı",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    {
      key: "description",
      name: "Açıklama",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.description || "").localeCompare(
          String(b.description || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        ),
    },
    {
      key: "pages",
      name: "Sayfa Erişimi",
      dataType: "number",
      id: 2,
      customSorter: (a, b) => {
        const score = (p) => (p?.fullAccess ? 9999 : (p?.pages || []).length);
        return score(a) - score(b);
      },
    },
    {
      key: "createdAt",
      name: translate("UI.CREATED"),
      dataType: "date",
      id: 3,
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    {
      key: "actions",
      name: translate("UI.ACTIONS"),
      dataType: "actions",
      id: 4,
      sortable: false,
    },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("user-profiles-list", headers);

  const addToast = (toast) => {
    const newToast = { ...toast, id: Date.now() };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(() => removeToast(newToast.id), 8000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const getAxiosConfig = () => {
    const config = { headers: { Authorization: `Bearer ${getToken()}` } };
    if (currentUser?.role === "admin" && selectedCompanyId) {
      config.params = { tenantId: selectedCompanyId };
    }
    return config;
  };

  const fetchProfiles = useCallback(async (tenantId = null) => {
    try {
      setLoading(true);
      setError(null);
      const uData = await getUserFromToken(true);
      setCurrentUser(uData);
      
      const effectiveTenantId = tenantId || uData.companyId || uData.company?.id;

      if (!effectiveTenantId && uData.role === "admin") {
        setProfiles([]);
        setLoading(false);
        return;
      }

      const params = {};
      if (effectiveTenantId && uData.role === "admin") {
         params.tenantId = effectiveTenantId;
      }

      const res = await axios.get(`${API_URL}/api/user-profiles`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params
      });
      setProfiles(res.data?.profiles || []);
    } catch (err) {
      setError(err.message);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, getUserFromToken]);

  const fetchCompanies = useCallback(async () => {
    try {
      const uData = await getUserFromToken(true);
      if (uData.role === "admin") {
        const res = await axios.get(`${API_URL}/api/system-companies`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setCompanies(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
    }
  }, [getToken, getUserFromToken]);

  useEffect(() => {
    fetchCompanies();
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchProfiles(selectedCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);


  const handleEdit = (profile) => {
    navigate(`/user-profiles/edit/${profile._id}`, { state: { tenantId: selectedCompanyId } });
  };

  const handleDelete = (profileId) => {
    confirm({
      title: "Profili Sil?",
      message:
        "Bu profili silmek istediğinize emin misiniz? Bu profile atanmış kullanıcıların profil ataması kaldırılacak.",
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE"),
      onConfirm: async () => {
        try {
          setLoading(true);
          const res = await axios.delete(
            `${API_URL}/api/user-profiles/${profileId}`,
            getAxiosConfig()
          );
          if (res.data.success) {
            addToast({
              title: "Başarılı!",
              description: "Profil silindi.",
              appearance: "success",
            });
            fetchProfiles(selectedCompanyId);
          }
        } catch (err) {
          addToast({
            title: "Hata!",
            description: err.response?.data?.message || "Profil silinemedi.",
            appearance: "danger",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const getPageCount = (profile) => {
    if (profile?.fullAccess) return "Sistem Yetkilisi";
    const pages = profile?.pages;
    if (!pages || pages.length === 0) return "Erişim yok";
    const readPages = pages.filter((p) => !p.endsWith(":write"));
    const writePages = pages.filter((p) => p.endsWith(":write"));
    return `${readPages.length} görüntüleme, ${writePages.length} düzenleme`;
  };

  return (
    <AppPage
      breadcrumbs={breadcrumbs}
      loading={loading}
    >
      <Container>
        <ContainerItem>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <h2 className="mb-0">Kullanıcı Profilleri</h2>
            </div>
            <div className="d-flex align-items-center gap-3">
              {currentUser?.role === "admin" && (
                <div style={{ minWidth: "250px" }}>
                  <Dropdown
                    placement="bottom-end"
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2 w-100" style={{ border: '1px solid #e2e5e8', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-2">
                          <Lozenge appearance={selectedCompanyId ? "success-subtle" : "neutral-subtle"}>
                            {selectedCompanyId
                              ? companies.find((c) => c._id === selectedCompanyId)?.name || "Firma Seçin"
                              : "-- Firma Seçin --"}
                          </Lozenge>
                        </div>
                      </div>
                    }
                  >
                    <DropdownContent className="w-100">
                      <DropdownItem
                        onClick={() => setSelectedCompanyId("")}
                        active={!selectedCompanyId}
                      >
                        -- Firma Seçin --
                      </DropdownItem>
                      {companies.map((c) => (
                        <DropdownItem
                          key={c._id}
                          onClick={() => setSelectedCompanyId(c._id)}
                          active={selectedCompanyId === c._id}
                        >
                          {c.name}
                        </DropdownItem>
                      ))}
                    </DropdownContent>
                  </Dropdown>
                </div>
              )}
              <Button
                title="Profil Ekle"
                appearance="primary-animated"
                icon={<FiPlus />}
                disabled={currentUser?.role === "admin" && !selectedCompanyId}
                onClick={() => {
                  navigate("/user-profiles/add", { state: { tenantId: selectedCompanyId } });
                }}
              />
            </div>
          </div>

          {error && <div className="alert alert-danger mb-4">{error}</div>}

          {currentUser?.role === "admin" && !selectedCompanyId ? (
            <div className="alert alert-info">
              Profilleri görüntülemek ve yönetmek için lütfen yukarıdan bir firma seçin.
            </div>
          ) : Array.isArray(profiles) && profiles.length > 0 ? (
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              loading={loading}
              emptyMessage="Profil bulunamadı"
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {profiles.map((profile, index) => (
                <Tr key={index} id={profile._id} data={profile}>
                  {visibleColumns.includes(0) && (
                    <Td>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <FiShield size={14} className="text-primary" />
                        <strong>{profile.name}</strong>
                        {profile.isSystem && (
                          <Lozenge appearance="success-subtle">Sistem</Lozenge>
                        )}
                      </div>
                    </Td>
                  )}
                  {visibleColumns.includes(1) && (
                    <Td>{profile.description || "-"}</Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>
                      <Lozenge appearance="info-subtle">
                        {getPageCount(profile)}
                      </Lozenge>
                    </Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td>
                      {profile.createdAt
                        ? new Date(profile.createdAt).toLocaleString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "-"}
                    </Td>
                  )}
                  {visibleColumns.includes(4) && (
                    <Td>
                      <div className="d-flex align-items-center gap-1">
                        <IconButton
                          icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                          appearance="subtle"
                          shape="circle"
                          onClick={() => handleEdit(profile)}
                        />
                        {!profile.isSystem && (
                          <IconButton
                            icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                            appearance="subtle"
                            shape="circle"
                            onClick={() => handleDelete(profile._id)}
                          />
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>
          ) : (
            <div className="alert alert-warning">
              Henüz profil oluşturulmamış.
            </div>
          )}
        </ContainerItem>
      </Container>
    </AppPage>
  );
};

export default withAuth(UserProfiles);
